import { type ForwardedRef, useEffect, useState } from "react";
import { Item, Section } from "react-stately";
import { Callout, CalloutIntent, Icons, MenuOptionItem, Select, type SelectProps } from "widgets";
import { forwardRef } from "widgets/utils";

import { ProjectColoredBadge } from "./ProjectColoredWidgets.tsx";
import type { MinimalProject } from "./projects-utils.ts";
import { type ProjectsFetchOptions, useFetchProjects } from "./useFetchProjects.ts";

type ProjectSelectProps<P extends MinimalProject> = ProjectsFetchOptions<P> &
  Pick<
    SelectProps<unknown>,
    | "name"
    | "label"
    | "aria-label"
    | "className"
    | "style"
    | "autoFocus"
    | "description"
    | "disabledKeys"
    | "errorMessage"
    | "isInvalid"
    | "icon"
    | "isDisabled"
    | "isRequired"
    | "placeholder"
    | "id"
  >;

const ProjectsSelectRender = <P extends MinimalProject>(
  props: ProjectSelectProps<P>,
  ref: ForwardedRef<HTMLButtonElement>,
) => {
  const {
    includeStagedProjects,
    includeAllProjects,
    defaultSelection,
    initialSelectedProjectId,
    onSelectionChange,
    isDisabled,
    placeholder,
  } = props;

  const { projectsList, projectsTree, isLoading, defaultSelectedProject, error } = useFetchProjects<P>({
    includeStagedProjects: includeStagedProjects,
    includeAllProjects: includeAllProjects,
    defaultSelection: defaultSelection ?? "private",
  });

  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  useEffect(() => {
    // sync (props.initialSelectedProjectId)
    if (initialSelectedProjectId != null) {
      setSelectedKey(String(initialSelectedProjectId));
      onSelectionChange?.(projectsList?.find((p) => p.id === initialSelectedProjectId));
    }
  }, [initialSelectedProjectId, onSelectionChange, projectsList]);

  useEffect(() => {
    // We don't want to reassign initialSelectedProjectId if defaultSelectedKey changes in the future
    if (initialSelectedProjectId == null && defaultSelectedProject) {
      setSelectedKey(String(defaultSelectedProject.id));
      onSelectionChange?.(defaultSelectedProject);
    }
  }, [initialSelectedProjectId, defaultSelectedProject, onSelectionChange]);

  if (error) {
    return <Callout title="Error fetching projects" intent={CalloutIntent.Danger} />;
  }

  return (
    <Select
      {...props}
      items={projectsTree}
      selectedKey={selectedKey != null ? String(selectedKey) : undefined}
      onSelectionChange={(key) => {
        const project = projectsList?.find((p) => p.id === Number(key));
        onSelectionChange?.(project);
        setSelectedKey(String(key));
      }}
      ref={ref}
      rendered={(selected) => selected.rendered}
      isDisabled={(isLoading && !projectsList) || isDisabled}
      placeholder={isLoading ? "Loading projects..." : placeholder}
    >
      {(section) => (
        <Section items={section.items} key={section.name} title={section.name}>
          {(item) => (
            <Item key={String(item.id)} textValue={item.name}>
              <MenuOptionItem
                title={item.name}
                icon={
                  item.color ? (
                    <ProjectColoredBadge project={item} noLink size="menu" />
                  ) : item.id === -1 ? (
                    <img src={Icons.Action.AllProjects} width={20} height={20} alt={`View All projects`} />
                  ) : (
                    "folder"
                  )
                }
              />
            </Item>
          )}
        </Section>
      )}
    </Select>
  );
};

export const ProjectsSelect = forwardRef(ProjectsSelectRender);
